import { requestAuthAdmin } from './auth';
import { DM_COUNTING_SCOPE } from '../utils/dmUsage';

export const DM_LIMIT_FIELDS = ['dm_daily_limit', 'dm_weekly_limit', 'dm_lifetime_limit'];
export const hasDmPolicy = (policy) => DM_LIMIT_FIELDS.every((field) => Object.prototype.hasOwnProperty.call(policy || {}, field) && (policy[field] === null || (Number.isSafeInteger(policy[field]) && policy[field] > 0)));

export const DM_OPERATING_LIMIT_FIELDS = ['dm_daily_limit', 'dm_monthly_limit', 'dm_lifetime_limit'];
export const hasDmMonthlyPolicy = policy => Object.prototype.hasOwnProperty.call(policy || {}, 'dm_monthly_limit') && (policy.dm_monthly_limit === null || (Number.isSafeInteger(policy.dm_monthly_limit) && policy.dm_monthly_limit > 0));
export const hasDmMonthlyMetrics = dm => Number.isSafeInteger(dm?.summary?.sent_month) && dm.summary.sent_month >= 0 && Number.isSafeInteger(dm?.at_limit?.monthly_users) && dm.at_limit.monthly_users >= 0 && Number.isSafeInteger(dm?.rejections?.monthly) && dm.rejections.monthly >= 0 && typeof dm?.monthly_reset_at === 'string' && Number.isFinite(Date.parse(dm.monthly_reset_at)) && Number.isSafeInteger(dm?.distributions?.monthly?.max) && dm.distributions.monthly.max >= 0 && Array.isArray(dm?.distributions?.monthly?.buckets) && dm.distributions.monthly.buckets.every(bucket => typeof bucket.range === 'string' && Number.isSafeInteger(bucket.count) && bucket.count >= 0);

export const CONTENT_USAGE_POLICY_PATH = '/api-admin/v0/content-usage/policy';
export const CONTENT_USAGE_METRICS_PATH = '/api-admin/v0/content-usage/metrics';
export const CONTENT_USAGE_AUDITS_PATH = '/api-admin/v0/content-usage/audits';

async function parseResponse(response) {
  const text = await response.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    // Keep the HTTP status useful when an upstream returns an empty or non-JSON body.
  }
  if (!response.ok) {
    const error = new Error(body?.msg || body?.message || body?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.code = body?.code;
    error.data = body?.data ?? body;
    if (response.status === 409) {
      error.currentUpdatedAt = body?.data?.current_updated_at || body?.current_updated_at;
    }
    throw error;
  }
  return body?.data ?? body;
}

async function request(path, options, accessToken) {
  const response = await requestAuthAdmin(path, options, accessToken);
  return parseResponse(response);
}

export function normalizePolicy(value) {
  if (!value || typeof value !== 'object') return value;
  const source = value.policy || value;
  return {
    ...value,
    ...source,
    story_current_limit: source.story_current_limit ?? null,
    speech_daily_limit: source.speech_daily_limit ?? null,
    speech_monthly_limit: source.speech_monthly_limit ?? null,
    speech_lifetime_limit: source.speech_lifetime_limit ?? null,
  };
}

export function normalizeMetrics(value, days) {
  const source = value?.metrics || value || {};
  const dm = source.dm;
  const count = (item) => Number.isSafeInteger(item) && item >= 0;
  const monthlyPresent = dm && [dm.summary?.sent_month, dm.at_limit?.monthly_users, dm.rejections?.monthly, dm.monthly_reset_at, dm.distributions?.monthly].some(value => value !== undefined);
  const validDm = (!monthlyPresent || hasDmMonthlyMetrics(dm)) && dm && typeof dm === 'object' &&
    dm.summary?.counting_scope === DM_COUNTING_SCOPE && (dm.summary.counted_since === null || (typeof dm.summary.counted_since === 'string' && Number.isFinite(Date.parse(dm.summary.counted_since)))) &&
    ['sent_today', 'sent_week', 'sent_lifetime', 'requests_today', 'requests_week', 'requests_in_window', 'active_senders'].every((key) => count(dm.summary?.[key])) &&
    ['daily_users', 'weekly_users', 'lifetime_users'].every((key) => count(dm.at_limit?.[key])) &&
    ['daily', 'weekly', 'lifetime', 'total'].every((key) => count(dm.rejections?.[key])) &&
    ['daily', 'weekly', 'lifetime'].every((key) => count(dm.distributions?.[key]?.max) && Array.isArray(dm.distributions[key].buckets) && dm.distributions[key].buckets.every((bucket) => typeof bucket.range === 'string' && count(bucket.count))) &&
    Array.isArray(dm.trends) && dm.trends.every((row) => typeof row.date === 'string' && ['sent', 'requests', 'rejected'].every((key) => count(row[key]))) &&
    ['daily_reset_at', 'weekly_reset_at'].every((key) => typeof dm[key] === 'string' && Number.isFinite(Date.parse(dm[key])));
  return {
    ...source,
    dm: validDm ? dm : undefined,
    dmContractError: dm != null && !validDm ? 'DM 집계 응답이 candidate 계약과 다릅니다. 실제 사용량을 확인할 수 없습니다.' : undefined,
    days: Number(source.days || String(source.window || '').replace(/d$/i, '') || days),
    summary: source.summary || {},
    distributions: source.distributions || source.distribution || {},
    at_limit: source.at_limit || source.users_at_limit || {},
    rejections: source.rejections || source.rejection_counts || {},
    trends: Array.isArray(source.trends) ? source.trends : (source.trend || source.daily || []),
  };
}

export function normalizeAudits(value) {
  const source = value || {};
  const items = Array.isArray(source) ? source : (source.items || source.audits || []);
  const state = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(raw); } catch { return {}; }
  };
  return {
    items: items.map((item) => ({
      ...item,
      before: state(item.before || item.before_policy || item.before_state),
      after: state(item.after || item.after_policy || item.after_state),
      created_at: item.created_at || item.updated_at,
    })),
    page: Number(source.page || 1),
    page_size: Number(source.page_size || items.length),
    total: Number(source.total || source.total_count || items.length),
  };
}

export function getContentUsagePolicy(accessToken) {
  return request(CONTENT_USAGE_POLICY_PATH, { method: 'GET' }, accessToken).then(normalizePolicy);
}

export function updateContentUsagePolicy(input, accessToken) {
  return request(CONTENT_USAGE_POLICY_PATH, {
    method: 'PUT',
    body: JSON.stringify(input),
  }, accessToken).then((value) => {
    const policy = normalizePolicy(value);
    const suppliedFields = [...DM_LIMIT_FIELDS, 'dm_monthly_limit'].filter(field => Object.prototype.hasOwnProperty.call(input, field));
    if (suppliedFields.some((field) => Object.prototype.hasOwnProperty.call(input, field)) &&
      (!hasDmPolicy(policy) || (suppliedFields.includes('dm_monthly_limit') && !hasDmMonthlyPolicy(policy)) || suppliedFields.some((field) => policy[field] !== input[field]))) {
      const error = new Error('DM 정책 반영 상태를 확인하지 못했습니다. 최신 정책을 다시 조회하고 검토하세요.');
      error.code = 'DM_POLICY_UNCONFIRMED';
      throw error;
    }
    return policy;
  });
}

export function getContentUsageMetrics(days, accessToken) {
  const path = `${CONTENT_USAGE_METRICS_PATH}?window=${encodeURIComponent(`${days}d`)}`;
  return request(path, { method: 'GET' }, accessToken).then((value) => normalizeMetrics(value, days));
}

export function getContentUsageAudits(accessToken) {
  return request(CONTENT_USAGE_AUDITS_PATH, { method: 'GET' }, accessToken).then(normalizeAudits);
}
